/**
 * Button Component
 *
 * Reusable button with multiple variants for different contexts.
 * Can render as a button, internal link, or external link depending on props.
 */

import { Component, JSX, Show, children, splitProps } from "solid-js";
import { A } from "@solidjs/router";
import styles from "./Button.module.css";

export type ButtonVariant =
  | "primary" // Inverts with theme (dark bg in light mode, light bg in dark mode)
  | "secondary" // Gradient background with white text, reduced opacity on hover
  | "onAccent" // Light bg, dark text (for accent-colored sections)
  | "text" // Plain text link style, no background (for standard backgrounds)
  | "textOnDark"; // Plain text link style for dark/accent backgrounds

/**
 * Checks if a URL is external (absolute URL with http/https)
 */
function isExternalUrl(url: string): boolean {
  return /^https?:\/\//.test(url);
}

export interface ButtonProps {
  /** Visual variant */
  variant?: ButtonVariant;
  /** Optional icon element to display before text */
  icon?: JSX.Element;
  /** Button text (optional for icon-only buttons) */
  children?: JSX.Element;
  /** Click handler */
  onClick?: () => void;
  /** Link URL - renders as <A> for internal routes, <a> for external URLs */
  href?: string;
  /** Link target (e.g., "_blank" for new tab) */
  target?: "_blank" | "_self" | "_parent" | "_top";
  /** Link rel attribute (auto-set to "noopener noreferrer" when target="_blank") */
  rel?: string;
  /** Force external link behavior (uses <a> instead of router <A>) */
  external?: boolean;
  /** Button type (ignored if href is provided) */
  type?: "button" | "submit" | "reset";
  /** Disabled state */
  disabled?: boolean;
  /** Additional CSS class */
  class?: string;
  /** Inline styles */
  style?: JSX.CSSProperties;
  /** Constrain width to fit content */
  fitContent?: boolean;
  /** Aria label for accessibility */
  "aria-label"?: string;
  /** Absorbed to prevent accidental pass-through disabling router preload */
  preload?: boolean;
}

/**
 * Button Component
 *
 * @example
 * // Primary button (default)
 * <Button onClick={handleClick}>Click me</Button>
 *
 * // Internal link (uses router for client-side navigation)
 * <Button href="/page" variant="secondary">Go to page</Button>
 *
 * // External link (auto-detected, uses regular <a> tag)
 * <Button href="https://example.com" target="_blank">Visit site</Button>
 *
 * // On accent background (light button)
 * <Button variant="onAccent" icon={<PlusIcon />}>Mehr anzeigen</Button>
 */
const Button: Component<ButtonProps> = (props) => {
  const [local, others] = splitProps(props, [
    "variant",
    "icon",
    "children",
    "onClick",
    "href",
    "target",
    "rel",
    "external",
    "type",
    "disabled",
    "class",
    "style",
    "fitContent",
    "preload",
  ]);

  const variant = () => local.variant || "primary";

  const buttonClass = () => `${styles.button} ${styles[variant()]} ${local.class || ""}`;

  const buttonStyle = () => ({
    ...local.style,
    ...(local.fitContent ? { width: "fit-content" } : {}),
  });

  // JSX passed as a prop compiles to a getter that creates a fresh element on
  // every access — reading it twice (truthiness check + render) desyncs SSR
  // hydration keys. Resolve it once via the children helper.
  const icon = children(() => local.icon);

  const content = (
    <>
      <Show when={icon()}>
        <span class={styles.icon}>{icon()}</span>
      </Show>
      <span class={styles.label}>{local.children}</span>
    </>
  );

  // Render as link if href is provided
  if (local.href) {
    const isExternal = local.external || isExternalUrl(local.href);

    // Auto-set rel for security when opening in new tab
    const relAttr = local.rel ?? (local.target === "_blank" ? "noopener noreferrer" : undefined);

    // External links use regular <a> tag
    if (isExternal) {
      return (
        <a
          href={local.href}
          class={buttonClass()}
          style={buttonStyle()}
          onClick={local.onClick}
          target={local.target}
          rel={relAttr}
          {...others}
        >
          {content}
        </a>
      );
    }

    // Internal links use router <A> for client-side navigation
    return (
      <A
        href={local.href}
        class={buttonClass()}
        style={buttonStyle()}
        onClick={local.onClick}
        target={local.target}
        rel={relAttr}
        {...others}
      >
        {content}
      </A>
    );
  }

  // Render as button
  return (
    <button
      type={local.type || "button"}
      class={buttonClass()}
      style={buttonStyle()}
      onClick={local.onClick}
      disabled={local.disabled}
      {...others}
    >
      {content}
    </button>
  );
};

export default Button;
